/**
 * Adds a contact with marketing preferences based on provided options.
 *
 * If the `smsConsent` checkbox is present and selected, the function sets the `emailLevel` to "all",
 * otherwise to "transactional". If a phone number is provided, it assumes SMS marketing opt-in and
 * sets the `smsLevel` accordingly.
 *
 * The function only calls the provided callback if a phone number is present or SMS consent is shown.
 *
 * @async
 * @function addContact
 * @param {Function} cb - A callback function to be invoked with the contact parameters.
 * @param {Object} options - Options to customize the contact creation.
 * @param {string} options.email - The user's email address.
 * @param {string} [options.phone] - The user's phone number (optional).
 * @param {boolean} [options.showSmsConsent=false] - Whether to show the SMS consent checkbox.
 * @param {boolean} [options.smsConsent=false] - Whether the user has given SMS marketing consent.
 * @param {string} options.signupSource - Identifier for the signup source.
 * @param {string} [options.gender] - The gender of the user.
 * @param {boolean} [options.isScrubs=false] - Whether the contact is part of a scrubs campaign.
 * @returns {Promise<undefined|*>} Returns the result of the callback if conditions are met, otherwise `undefined`.
 */
export const addContact = async (cb, options) => {
  /**
   * If the sms consent box is present then we must make sure that email and phone communications
   * are set to the approiate setting so the customer does not recieve marketing content
   */
  const addContactParameters = {
    firstName: '',
    lastName: '',
    email: options.email,
    emailLevel: 'all',
    attributes: {},
    signupSourceId: options.signupSource,
    isScrubs: options.isScrubs || false,
    gender: options.gender,
  };

  /**
   * If phone is present then the customer has opted into sms marketing
   */
  if (options.phone) {
    addContactParameters.smsNumber = options.phone.replace(/[^\d+]/g, '');
    addContactParameters.smsLevel = 'all';
  }

  if (options.showSmsConsent && !options.smsConsent) {
    // transactional means only messages for orders
    addContactParameters.emailLevel = 'transactional';
    addContactParameters.smsLevel = 'transactional';
  }

  if (options.phone || options.showSmsConsent) {
    return cb(addContactParameters);
  }
};
